import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { ResearchDetail } from "../pages/DeepResearch/types";

interface ResearchProgressState {
  currentProgress: number;
  currentMessage: string;
  currentStatus?: string; // ストリーミングからの最新ステータス
  searchCount: number;
  maxSearches: number;
  currentQuery?: string;
}

interface ResearchContextType {
  // Research state
  research: ResearchDetail | null;
  setResearch: React.Dispatch<React.SetStateAction<ResearchDetail | null>>;

  // Progress state
  progressState: ResearchProgressState;
  updateProgress: (progress: Partial<ResearchProgressState>) => void;
  resetProgress: () => void;

  // Tab state
  activeTab: number;
  setActiveTab: (tab: number) => void;

  // UI state
  editingPlan: boolean;
  setEditingPlan: (editing: boolean) => void;
  approving: boolean;
  setApproving: (approving: boolean) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  error: string;
  setError: (error: string) => void;

  // Force update for real-time updates
  forceUpdate: number;
  triggerForceUpdate: () => void;
}

const ResearchContext = createContext<ResearchContextType | undefined>(undefined);

export const useResearch = () => {
  const context = useContext(ResearchContext);
  if (!context) {
    throw new Error("useResearch must be used within a ResearchProvider");
  }
  return context;
};

interface ResearchProviderProps {
  children: ReactNode;
}

export const ResearchProvider: React.FC<ResearchProviderProps> = ({ children }) => {
  const [research, setResearch] = useState<ResearchDetail | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [editingPlan, setEditingPlan] = useState(false);
  const [approving, setApproving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0);

  const [progressState, setProgressState] = useState<ResearchProgressState>({
    currentProgress: 0,
    currentMessage: "",
    searchCount: 0,
    maxSearches: 0,
  });

  const updateProgress = useCallback((progress: Partial<ResearchProgressState>) => {
    setProgressState((prev) => ({
      ...prev,
      ...progress,
    }));
  }, []);

  const resetProgress = useCallback(() => {
    setProgressState({
      currentProgress: 0,
      currentMessage: "",
      searchCount: 0,
      maxSearches: 0,
    });
  }, []);

  const triggerForceUpdate = useCallback(() => {
    setForceUpdate((prev) => prev + 1);
  }, []);

  const value: ResearchContextType = {
    // Research state
    research,
    setResearch,

    // Progress state
    progressState,
    updateProgress,
    resetProgress,

    // Tab state
    activeTab,
    setActiveTab,

    // UI state
    editingPlan,
    setEditingPlan,
    approving,
    setApproving,
    loading,
    setLoading,
    error,
    setError,

    // Force update
    forceUpdate,
    triggerForceUpdate,
  };

  return <ResearchContext.Provider value={value}>{children}</ResearchContext.Provider>;
};
